/**
 * Loss Runs section page (OR-1174).
 *
 * Layout mirrors the Schedule section: section header (title + dates),
 * the 4-card summary strip, then the tabbed claims card. Unlike
 * Schedule this page is NOT react-hook-form driven — the Loss Runs
 * table is a staged SoT (`useLossRuns`); Save & Continue validates the
 * rows, flushes them, and advances to the next section.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Calendar } from "@untitledui/icons";
import { SubmissionFooter } from "../../../components/submission-footer";
import { useFacilities } from "../../../hooks/use-facilities";
import { refreshLossRuns, useLossRuns } from "../../../hooks/use-loss-runs";
import { useDebouncedAutosave } from "../../../hooks/use-debounced-autosave";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";
import { lossRunsAreValid, validateLossRunRow } from "./loss-run-validation";
import { LossRunsTabs } from "./sections/loss-runs-tabs";
import { LossRunsSummaryCards } from "./sections/summary-cards";

const SECTION_ID = "loss-runs";

export function LossRunsPage() {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    /** Flips to true on a failed Save attempt so the table's cells show
     *  their validation errors (mirrors the rhf sections' submit-time
     *  feedback). */
    const [showErrors, setShowErrors] = useState(false);

    const { state, save, isDirty } = useLossRuns(detail.id);
    // Facility Archive/Restore (a GLOBAL action, offered on this page's row
    // menus alongside claim-level archive) rides this page's manual Save,
    // same as Schedule/Regulatory/Operational — unlike claims, which autosave
    // below via `useDebouncedAutosave`.
    const { save: saveFacilities, isDirty: facilitiesDirty } = useFacilities(detail.id);

    // Background refresh on mount: other sections change values this store
    // displays (Schedule's Retention drives every matched row's SIR by
    // Community — OR-1337), and the shared cache would otherwise show them
    // stale until a hard reload. Page-level (not in the hook) so the tabs
    // and cards mounting/unmounting don't re-fetch.
    useEffect(() => {
        if (detail.id) void refreshLossRuns(detail.id);
    }, [detail.id]);

    // Annual Loss Summary + Loss Ratio Analysis are read-only, fed by the
    // backend `/loss-runs/summary` via `useLossRuns` — no page-level state.
    const next = getNextSection(SECTION_ID);
    const renewal = detail.accountBasics.renewalDate || "";
    const expiration = detail.accountBasics.expirationDate || "";

    // Auto-save on every user edit (debounced) — Henry/Andressa jun-22, replaces
    // the manual save. `save()` flushes the staged diff + re-fetches the summary,
    // so the cards/tabs update in place. Only fires when the staged rows are
    // VALID (a half-filled new row pauses it until completed → no 422), and
    // never on the initial load (the hook skips the baseline signature).
    const rows = useMemo(() => (state.status === "ready" ? state.data : []), [state]);
    // Archiving a claim only flips its own `status`, so the rows signature
    // already covers it — no extra store to flush from this page.
    const autosaveSignature = JSON.stringify(rows);
    // Auto-save persists only the rows that are valid — a half-filled new row
    // is left staged (no 422) and, crucially, doesn't block saving the valid
    // rows around it. The whole-section validity gate was too strict.
    const autosave = useCallback(
        () => save({ savable: (row) => Object.keys(validateLossRunRow(row, { requireAll: true })).length === 0 }),
        [save],
    );
    useDebouncedAutosave(autosaveSignature, autosave, {
        enabled: Boolean(detail.id) && state.status === "ready",
    });

    // Save validates the rows + flushes them (no navigation). Continue
    // just advances — the footer guards against leaving while dirty.
    const onSave = useCallback(async () => {
        if (!lossRunsAreValid(rows)) {
            setShowErrors(true);
            // Scroll the first invalid cell into view, matching the
            // Schedule section's submit-error behaviour.
            requestAnimationFrame(() => {
                const first = document.querySelector<HTMLElement>('[aria-invalid="true"]');
                if (!first) return;
                first.scrollIntoView({ behavior: "smooth", block: "center" });
                first.focus?.({ preventScroll: true });
            });
            return;
        }
        // Disarm the strict pass once the rows validate again — leaving it on
        // meant any later transient invalid state (e.g. a half-typed date)
        // flashed error tooltips forever after one failed attempt.
        setShowErrors(false);
        setIsSaving(true);
        try {
            await save();
            if (facilitiesDirty) {
                // `save()` above skips its own re-fetch when only facility
                // archive/restore changed (no claim edits, so its working
                // copy already matches its baseline) — refresh explicitly so
                // the summary cards/totals pick up the newly (ex)cluded
                // facility's claims. Only on success: a failed save leaves
                // the backend state unchanged, so refetching would be a
                // wasted round-trip and could misreport a saved state.
                const facilitiesOk = await saveFacilities();
                if (facilitiesOk) await refreshLossRuns(detail.id);
            }
        } finally {
            setIsSaving(false);
        }
    }, [rows, save, facilitiesDirty, saveFacilities, detail.id]);
    const onContinue = () => navigate(next ? `../${next.path}` : "/submissions");

    return (
        // lg+: fill the area between the cards and the footer (the table flexes
        // + scrolls internally). Mobile: natural height, the page scrolls.
        <div className="flex flex-col lg:min-h-0 lg:flex-1">
            <div className="flex w-full max-w-dashboard flex-col gap-6 lg:min-h-0 lg:flex-1 lg:pb-4">
                <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-primary text-md font-semibold">Loss Runs</h2>
                        <p className="text-tertiary text-sm">Claims data and loss analysis based on loss runs</p>
                    </div>
                    {/* Mobile: dates spread 50/50, divider hidden. md+:
                      * content-width with divider, tight to the title. */}
                    <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
                        <DateField label="Renewal Date" value={renewal} />
                        <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
                        <DateField label="Expiration Date" value={expiration} />
                    </div>
                </header>

                <LossRunsSummaryCards submissionId={detail.id} />
                <LossRunsTabs submissionId={detail.id} showErrors={showErrors} />
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isDirty || facilitiesDirty}
                isSaving={isSaving}
                nextSectionLabel={next?.label ?? null}
            />
        </div>
    );
}

function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}
